// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Button,
  Form,
  FormGroup,
  CheckboxField,
  notificationConnect,
  Divider,
} from '@livechat/design-system'
import colors from '@livechat/design-system-colors'
import firebase from '../firebase'
import replyInImage from '../reply-in.png'
import notificationOnlyImage from '../notification-only.png'
import IntegrationApiClient from '../api/integration'

const Settings = ({ auth, notificationSystem }) => {
  const [formEdited, setFormEdited] = useState(false)
  const [isFormSaving, setIsFormSaving] = useState(false)
  const [notificationsOnly, setNotificationsOnly] = useState(
    auth.slack.only_notifications
  )
  const [initForCustomerOnly, setInitForCustomerOnly] = useState(
    auth.slack.init_only_for_customer
  )

  const [initForChatbotTransfer, setInitForChatbotTransfer] = useState(
    auth.slack.init_for_chatbot_transfer
  )

  const [isGenericAgent, setIsGenericAgent] = useState(
    auth.isGenericAgent || false
  )

  const [useSlackThreads, setUseSlackThreads] = useState(
    auth.slack.use_slack_threads || false
  )

  const integrationApi = new IntegrationApiClient()

  const saveForm = async () => {
    try {
      setIsFormSaving(true)

      let firebaseApp

      switch (auth.appName) {
        case 'slack3':
          firebaseApp = firebase.slack3
          break

        case 'slack2':
          firebaseApp = firebase.slack2
          break

        default:
          firebaseApp = firebase.slack
      }
      const db = firebaseApp.firestore()
      const licensesRef = db
        .collection('licenses')
        .doc(`${auth.agentData.license}`)
      const doc = await licensesRef.get()
      const slack = doc.exists ? doc.data().slack : []
      const newSlack = {
        ...slack,
        only_notifications: notificationsOnly,
        init_only_for_customer: initForCustomerOnly,
        init_for_chatbot_transfer: initForChatbotTransfer,
        use_slack_threads: useSlackThreads,
      }

      await db
        .collection('licenses')
        .doc(`${auth.agentData.license}`)
        .update({
          slack: newSlack,
          is_generic_agent: isGenericAgent,
        })
      setIsFormSaving(false)

      notificationSystem.add({
        type: 'toast',
        autoHideDelayTime: 3000,
        payload: {
          variant: 'success',
          content: 'Saved successfully',
          removable: true,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        },
      })

      await integrationApi.refreshSlack(auth.firebaseToken, auth.appName)
    } catch (err) {
      setIsFormSaving(false)
      notificationSystem.add({
        type: 'error',
        autoHideDelayTime: 3000,
        payload: {
          variant: 'success',
          content: 'An error occured while saving',
          removable: true,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        },
      })
      console.log('err')
    }
  }

  return (
    <div
      css={css`
        padding: 20px;

        h3.lc-form__label {
          display: none;
        }

        .lc-form__helper {
          margin-top: 0;
          margin-bottom: 12px;
          color: ${colors.gray900};
        }

        .lc-form-group {
          margin-bottom: 12px;
        }
      `}
    >
      <Form
        labelText="Reply To Settings"
        helperText=""
        noValidate
        formFooter={
          <Button
            primary
            disabled={!formEdited}
            onClick={saveForm}
            loading={isFormSaving}
          >
            Save changes
          </Button>
        }
      >
        <FormGroup>
          <CheckboxField
            checked={notificationsOnly}
            onClick={() => {
              setFormEdited(true)
              setNotificationsOnly(!notificationsOnly)
            }}
            id="notifications_only"
            description={`By default, the integration will send the "welcome notification" with options to reply in public or private channel. In order to receive only notifications about started chat, check the box above.`}
          >
            Notifications only
          </CheckboxField>
        </FormGroup>

        <FormGroup>
          <img
            src={notificationsOnly ? notificationOnlyImage : replyInImage}
            css={css`
              width: 60%;
            `}
            alt="Reply in version of welcome card"
          />
        </FormGroup>

        <Divider />

        <FormGroup>
          <CheckboxField
            checked={isGenericAgent}
            onClick={() => {
              setFormEdited(true)
              setIsGenericAgent(!isGenericAgent)
            }}
            id="is_generic_agent"
            description={`Use "Support Agent" name and a generic avatar, otherwise Slack user name and avatar will be used.`}
          >
            Use Support Agent
          </CheckboxField>
        </FormGroup>

        <Divider />

        <FormGroup>
          <CheckboxField
            checked={initForCustomerOnly}
            onClick={() => {
              setFormEdited(true)
              setInitForCustomerOnly(!initForCustomerOnly)
              setInitForChatbotTransfer(false)
            }}
            id="init_only_for_customer"
            description={`Initialise Slack "welcome notification" only for customer input. By default Slack sends "welcome notification" when chat starts (Greetings or Chat bots).`}
          >
            Initialise Slack for customer input only
          </CheckboxField>

          <Divider />
        </FormGroup>

        <FormGroup>
          <CheckboxField
            checked={initForChatbotTransfer}
            onClick={() => {
              setFormEdited(true)
              setInitForChatbotTransfer(!initForChatbotTransfer)
              setInitForCustomerOnly(false)
            }}
            id="init_for_chatbot_transfer"
            description={`Initialise Slack "welcome notification" only when "chatbot-transfer" tag is created and a customer sends a message.`}
          >
            Initialise Slack for "Transfer chat" (Only for Chatbot)
          </CheckboxField>

          <Divider />
        </FormGroup>

        {auth.agentData.license === 10414122 && (
          <FormGroup>
            <CheckboxField
              checked={useSlackThreads}
              onClick={() => {
                setFormEdited(true)
                setUseSlackThreads(!useSlackThreads)
              }}
              id="use_slack_threads"
              description={`Use Slack threads for replies.`}
            >
              Use Slack threads
            </CheckboxField>

            <Divider />
          </FormGroup>
        )}
      </Form>
    </div>
  )
}

export default notificationConnect(Settings)
